<template>
    <BookingFormLayout
        v-bind="
            bookingFormSharedData
                .setContainerClasses('max-w-5xl')
                .getBookingFormLayoutProps()
        "
    >
        <template #content>
            <SchedulingErrorMessage
                v-if="errorMessage"
                :errorMessage="errorMessage"
            />

            <BorderSeparatedSections
                mainContainerClasses="grid grid-cols-12"
                leftContainerClasses="col-span-12 lg:col-span-4"
                rightContainerClasses="col-span-12 lg:col-span-8"
            >
                <template #left>
                    <MeetingSelectedDetails
                        :company="localCompany"
                        :service="localServiceAggregate.service"
                        :user="localCompanyUserAggregate.user"
                        :enabledLocationTypes="
                            localServiceAggregate.getUniqueEnabledMeetingLocationTypes()
                        "
                        :meetingLocationIconSize="20"
                        :recurrenceSettings="
                            localServiceAggregate.service.recurrenceSettings
                        "
                        :localizer="localizer"
                    />
                </template>
                <template #right>
                    <div class="space-y-4">
                        <div>
                            <ElementWithIdOnMobile :id="CALENDAR_SECTION_ID" />
                            <BookingFormTitle>
                                {{
                                    localizer.translate(
                                        TextTokenEnum.BookingForm_SelectADateAndTime,
                                    )
                                }}
                            </BookingFormTitle>
                        </div>

                        <div
                            class="grid grid-cols-11 gap-4 space-x-0 lg:space-x-1 space-y-4 lg:space-y-0"
                        >
                            <div class="col-span-11 lg:col-span-7 space-y-2">
                                <TimezoneSelector
                                    :value="selectedTimeZone"
                                    :useBookingFormThemeCssVariables="true"
                                    :required="true"
                                    @onSelectionChange="
                                        handleTimeZoneSelectionChange(
                                            $event.value,
                                        )
                                    "
                                />

                                <AvailabilityCalendar
                                    :months="availabilityResult.months"
                                    :selectedDate="
                                        selectedAvailabilityResultDate?.dateInSelectedTimeZoneForDisplay ??
                                        undefined
                                    "
                                    :isLoading="isLoading"
                                    :useBookingFormThemeCssVariables="true"
                                    @onDateSelectionChange="
                                        handleDateSelectionChange($event.date)
                                    "
                                    @onMonthSelectionChange="
                                        handleMonthSelectionChanged()
                                    "
                                    :localizer="localizer"
                                />
                            </div>
                            <template v-if="selectedAvailabilityResultDate">
                                <div class="col-span-11 lg:col-span-4">
                                    <ElementWithIdOnMobile
                                        :id="TIMESLOT_LIST_SECTION_ID"
                                    />

                                    <div class="flex flex-col gap-2">
                                        <TimeslotFormatSelector
                                            :currentTimeslotTimeFormat="
                                                selectedTimeslotFormat
                                            "
                                            @onChange="
                                                handleTimeslotFormatChange(
                                                    $event.format,
                                                )
                                            "
                                        />

                                        <TimeSlotList
                                            :availabilityResultDate="
                                                selectedAvailabilityResultDate
                                            "
                                            :shouldRenderAvailableSeats="
                                                localServiceAggregate.service.supportsMultipleAttendeesPerMeeting()
                                            "
                                            :selectedAvailabilityResultTimeSlot="
                                                selectedAvailabilityResultTimeSlot
                                            "
                                            :useBookingFormThemeCssVariables="
                                                true
                                            "
                                            @onTimeSlotSelectionChange="
                                                handleTimeSlotSelectionChanged(
                                                    $event.availabilityResultTimeSlot,
                                                )
                                            "
                                            :timeslotTimeFormat="
                                                selectedTimeslotFormat
                                            "
                                        />
                                    </div>
                                </div>
                            </template>
                        </div>

                        <div class="flex justify-end">
                            <form
                                method="POST"
                                :action="formUrl"
                            >
                                <Button
                                    type="submit"
                                    :disabled="
                                        !selectedAvailabilityResultTimeSlot ||
                                        !selectedAvailabilityResultDate ||
                                        !selectedTimeZone
                                    "
                                    :useBookingFormThemeCssVariables="true"
                                    iconName="arrow-right"
                                    :iconRight="true"
                                >
                                    {{
                                        localizer.translate(
                                            TextTokenEnum.BookingForm_Continue,
                                        )
                                    }}
                                </Button>
                            </form>
                        </div>
                    </div>
                </template>
            </BorderSeparatedSections>
        </template>
    </BookingFormLayout>
</template>

<script setup lang="ts">
    /**
     * TODO: Refactor this page to use regular inputs inside the <form> tag, instead of binding stuff to variables
     */

    import { onBeforeMount, ref, computed, nextTick, onMounted } from 'vue'
    import AvailabilityCalendar from '@/BookingForm/BookingForm/Component/AvailabilityCalendar.vue'
    import TimeSlotList from '@/BookingForm/BookingForm/Component/TimeSlotList.vue'
    import Button from '@/Shared/Component/Button.vue'
    import useApiRequest from '@/Shared/Hook/useApiRequest'
    import BookingFormLayout from '@/Shared/Layout/BookingForm/BookingFormLayout.vue'
    import MeetingSelectedDetails from '@/BookingForm/BookingForm/Page/Step/MeetingSelectedDetails.vue'
    import {
        ResponseJsonKeyEnum,
        type AvailabilityResultDateType,
        type AvailabilityResultTimeSlotType,
    } from '@/Shared/AutoGenerated/Main'
    import { UrlFactory } from '@/Shared/Url/UrlFactory'
    import useDate, { TimeslotTimeFormat } from '@/Shared/Hook/useDate'
    import ApiResponseType from '@/Shared/Hook/ApiResponseType'
    import BookingFormSharedData from '@/Shared/Entity/BookingFormSharedData'
    import AvailabilityResult from '@/App/Availability/Entity/AvailabilityResult'
    import TimezoneSelector from '@/BookingForm/BookingForm/Component/TimezoneSelector.vue'
    import BorderSeparatedSections from '@/Shared/Layout/BookingForm/BorderSeparatedSections.vue'
    import SchedulingErrorMessage from '@/BookingForm/BookingForm/Component/SchedulingErrorMessage.vue'
    import BookingFormTitle from '@/Shared/Component/BookingFormTitle.vue'
    import usePageScroll from '@/Shared/Hook/usePageScroll'
    import ElementWithIdOnMobile from '@/BookingForm/BookingForm/Component/ElementWithIdOnMobile.vue'
    import LocalizerService from '@/Shared/Service/Localization/LocalizerService'
    import {
        TimeZoneEnum,
        TimeZoneEnumList,
    } from '@/Shared/AutoGenerated/TimeZoneEnum'
    import { TextTokenEnum } from '@/Shared/AutoGenerated/LocalizationEnum'
    import Company from '@/App/Company/Entity/Company'
    import ServiceAggregate from '@/App/Service/Entity/ServiceAggregate'
    import CompanyUserAggregate from '@/App/CompanyUser/Entity/CompanyUserAggregate'
    import DraftMeeting from '@/App/Booking/Draft/Entity/DraftMeeting'
    import TimeslotFormatSelector from '@/BookingForm/BookingForm/Component/TimeslotFormatSelector.vue'

    interface ShowBookingFormDateStepProps {
        bookingFormSharedDataData: BookingFormSharedData
        draftMeetingData: any
        companyData: any
        serviceAggregateData: any
        companyUserAggregateData: any
    }

    const props = defineProps<ShowBookingFormDateStepProps>()
    const bookingFormSharedData = BookingFormSharedData.hydrate(
        props.bookingFormSharedDataData,
    )
    const localizer = LocalizerService.make(bookingFormSharedData.currentLocale)

    // -------------------------
    // State
    // -------------------------

    const isLoading = ref<boolean>(true)
    const errorMessage = ref<string | null>(null)

    const browserTimeZoneResult = useDate().getBrowserTimezoneWithFallback(
        TimeZoneEnumList,
        TimeZoneEnum.UTC,
    )
    const browserTimeZone = browserTimeZoneResult.timezoneEntry.key

    const localCompany = ref(Company.hydrateSingle(props.companyData))
    const localServiceAggregate = ref(
        ServiceAggregate.hydrateSingle(props.serviceAggregateData),
    )
    const localCompanyUserAggregate = ref(
        CompanyUserAggregate.hydrateSingle(props.companyUserAggregateData),
    )
    const localDraftMeeting = ref(
        DraftMeeting.hydrateSingle(props.draftMeetingData),
    )

    const availabilityResult = ref<AvailabilityResult>(
        AvailabilityResult.newEmpty(),
    )

    const selectedAvailabilityResultDate =
        ref<AvailabilityResultDateType | null>(null)
    const selectedAvailabilityResultTimeSlot = ref<
        AvailabilityResultTimeSlotType | undefined
    >(undefined)
    const selectedTimeZone = ref<string>(
        localDraftMeeting.value.selectedTimeZone ?? browserTimeZone,
    )

    const urlFactory = new UrlFactory()
    const formUrl = computed((): string => {
        return urlFactory.makeBookingFormDateUrl(
            localCompany.value.slug,
            localServiceAggregate.value.service.slug,
            localDraftMeeting.value.id.value,
            selectedTimeZone.value,
            selectedAvailabilityResultDate.value
                ?.dateInSelectedTimeZoneForDisplay ?? '',
            selectedAvailabilityResultTimeSlot.value
                ?.timeSlotInSelectedTimeZoneForDisplay.start ?? '',
            selectedAvailabilityResultTimeSlot.value
                ?.timeSlotInSelectedTimeZoneForDisplay.end ?? '',
            selectedAvailabilityResultDate.value
                ?.dateInAvailabilityTimeZoneForScheduling ?? '',
            selectedAvailabilityResultTimeSlot.value
                ?.timeSlotInAvailabilityTimeZoneForScheduling.start ?? '',
            selectedAvailabilityResultTimeSlot.value
                ?.timeSlotInAvailabilityTimeZoneForScheduling.end ?? '',
            bookingFormSharedData.currentLocale,
        )
    })

    // -------------------------
    // Event handlers
    // -------------------------

    const handleTimeZoneSelectionChange = (timeZone: string | null) => {
        if (typeof timeZone !== 'string') {
            throw new Error('Timezone should be a string', { cause: timeZone })
        }

        // Prevent a new request if the timezone didn't change
        if (timeZone === selectedTimeZone.value) {
            return
        }

        selectedAvailabilityResultDate.value = null
        selectedAvailabilityResultTimeSlot.value = undefined
        selectedTimeZone.value = timeZone

        fetchAvailableDates(selectedTimeZone.value)
    }

    const handleDateSelectionChange = (value: string | null) => {
        if (!value) {
            selectedAvailabilityResultDate.value = null
            selectedAvailabilityResultTimeSlot.value = undefined

            return
        }

        // Prevent a new request if the date didn't change
        if (
            value ===
            selectedAvailabilityResultDate.value
                ?.dateInSelectedTimeZoneForDisplay
        ) {
            return
        }

        selectedAvailabilityResultDate.value =
            availabilityResult.value.getAvailabilityResultDate(value)
        selectedAvailabilityResultTimeSlot.value = undefined

        scrollToTimeslotsSection()
    }

    const handleTimeSlotSelectionChanged = (
        availabilityResultTimeSlot: AvailabilityResultTimeSlotType | null,
    ) => {
        if (!availabilityResultTimeSlot) {
            return
        }

        selectedAvailabilityResultTimeSlot.value = availabilityResultTimeSlot
    }

    const handleMonthSelectionChanged = () => {
        selectedAvailabilityResultDate.value = null
        selectedAvailabilityResultTimeSlot.value = undefined
    }

    // -------------------------
    // API calls
    // -------------------------

    const fetchAvailableDates = (timeZone: string) => {
        isLoading.value = true
        errorMessage.value = null

        selectedAvailabilityResultDate.value = null
        selectedAvailabilityResultTimeSlot.value = undefined

        const url = urlFactory.makeBookingFormAvailableDatesUrl(
            localCompany.value.slug,
            localServiceAggregate.value.service.slug,
            localDraftMeeting.value.id.value,
            timeZone,
        )

        useApiRequest()
            .get(url)
            .then((response: ApiResponseType) => {
                const calendarMonths = response.getHydrateCalendarMonths(
                    ResponseJsonKeyEnum.BookingForm_Scheduling_IndexAvailableDates_CalendarMonths,
                )
                const availabilityResultDates =
                    response.getHydratedAvailabilityResultDates(
                        ResponseJsonKeyEnum.BookingForm_Scheduling_IndexAvailableDates_AvailabilityResultDates,
                    )

                availabilityResult.value = new AvailabilityResult(
                    calendarMonths,
                    availabilityResultDates,
                )
            })
            .catch((error) => {
                errorMessage.value =
                    'Error when trying to list available dates.'

                console.error(error)
            })
            .finally(() => {
                isLoading.value = false
            })
    }

    // -------------------------
    // Lifecycle hooks
    // -------------------------

    onBeforeMount(() => {
        fetchAvailableDates(browserTimeZone)
    })

    onMounted(() => {
        scrollToCalendarSection()
    })

    // -------------------------
    // Scroll to calendar sectio on mobile
    // -------------------------

    const TIMESLOT_LIST_SECTION_ID = 'TIMESLOT_LIST_SECTION_ID'
    const CALENDAR_SECTION_ID = 'CALENDAR_SECTION_ID'

    const scrollToCalendarSection = (): void => {
        usePageScroll().scrollToElement(CALENDAR_SECTION_ID)
    }

    const scrollToTimeslotsSection = (): void => {
        nextTick(() => {
            usePageScroll().scrollToElement(TIMESLOT_LIST_SECTION_ID)
        })
    }

    // -------------------------
    // Timeslot format selection
    // -------------------------

    const selectedTimeslotFormat = ref<TimeslotTimeFormat>(
        TimeslotTimeFormat.Format24Hours,
    )

    const handleTimeslotFormatChange = (value: TimeslotTimeFormat) => {
        selectedTimeslotFormat.value = value
    }
</script>
